import type { TravelOfferDiagOtaUrls } from '@shared/types/TravelOfferDiag.types';
import { getCurrencyDecimalPlaces } from '@shared/utils/currency';
import type { DetailItemView, FormatCurrencyFn, OtaLinkView } from './DynamicMargin.types';

export const DASH = '—';

const OTA_PROVIDER_KEYS = ['booking', 'expedia'] as const;

export const parseNumericValue = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsedValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

export const formatNumericString = (value: number): string => {
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
};

export const normalizePercentValue = (value: number | null | undefined): number | null => {
  if (value === null || value === undefined) {
    return null;
  }

  return value !== 0 && Math.abs(value) < 1 ? value * 100 : value;
};

export const formatPercentValue = (value: number | null | undefined): string => {
  const normalizedValue = normalizePercentValue(value);

  if (normalizedValue === null) {
    return DASH;
  }

  return `${formatNumericString(normalizedValue)}%`;
};

export const formatTextValue = (value: string | null | undefined): string => {
  if (!value) {
    return DASH;
  }

  const trimmedValue = value.trim();
  return trimmedValue ? trimmedValue : DASH;
};

// Short labels used in the diagnostics comparison table, where horizontal space is tight.
const PROVIDER_SHORT_LABELS: Record<string, string> = {
  rategain: 'rg',
};

const SUPPLIER_SOURCE_SHORT_LABELS: Record<string, string> = {
  bookingcom: 'booking',
  expedia: 'expedia',
  google: 'google',
};

const shortLabel = (value: string, labels: Record<string, string>): string => {
  const trimmedValue = value.trim();
  return labels[trimmedValue.toLowerCase()] ?? trimmedValue;
};

// Combines provider and supplierSource into a compact label, e.g. rategain + bookingcom -> "rg / booking".
// Falls back to whichever value is present (or DASH) when only one is available or both are identical.
export const formatProviderLabel = (provider: string | null | undefined, supplierSource: string | null | undefined): string => {
  const providerLabel = provider?.trim() ? shortLabel(provider, PROVIDER_SHORT_LABELS) : '';
  const supplierLabel = supplierSource?.trim() ? shortLabel(supplierSource, SUPPLIER_SOURCE_SHORT_LABELS) : '';

  if (providerLabel && supplierLabel && providerLabel.toLowerCase() !== supplierLabel.toLowerCase()) {
    return `${providerLabel} / ${supplierLabel}`;
  }

  return providerLabel || supplierLabel || DASH;
};

export const formatBooleanValue = (value: boolean | null | undefined, yesLabel: string, noLabel: string): string => {
  if (value === null || value === undefined) {
    return DASH;
  }

  return value ? yesLabel : noLabel;
};

export const formatMoneyValue = (formatCurrency: FormatCurrencyFn, currency: string, value: string | number | null | undefined): string => {
  const parsedValue = parseNumericValue(value);
  if (parsedValue === null) {
    return DASH;
  }

  const precision = getCurrencyDecimalPlaces(currency);
  const floatingPointDecimalString = typeof value === 'string' && Number.isFinite(Number(value)) ? value : parsedValue.toFixed(precision);

  return formatCurrency({
    priceFloatingPointDecimalString: floatingPointDecimalString,
    priceCurrencyCode: currency,
    formatForm: 'none',
    formatPrecision: precision,
  });
};

export const formatCalculatedMoneyValue = (formatCurrency: FormatCurrencyFn, currency: string, value: number | null): string => {
  if (value === null) {
    return DASH;
  }

  return formatMoneyValue(formatCurrency, currency, value);
};

export const calculateDifference = (minuend: string | number | null | undefined, subtrahend: string | number | null | undefined): number | null => {
  const minuendValue = parseNumericValue(minuend);
  const subtrahendValue = parseNumericValue(subtrahend);

  if (minuendValue === null || subtrahendValue === null) {
    return null;
  }

  return minuendValue - subtrahendValue;
};

export const createDetailItem = (label: string, value: string): DetailItemView | null => {
  return value === DASH ? null : { label, value };
};

export const getOtaLinks = (otaUrls?: TravelOfferDiagOtaUrls | null): ReadonlyArray<OtaLinkView> => {
  if (!otaUrls) {
    return [];
  }

  return OTA_PROVIDER_KEYS.flatMap(provider => {
    const href = otaUrls[provider];
    return typeof href === 'string' && href ? [{ href, label: provider }] : [];
  });
};
